import type { ResolveBenchmarksRequestBodyChannel } from '@dashhudson/api-client/library/model';

import type {
  PerformanceAlertMediaType,
  PerformanceAlertSourceType,
} from '@/app/settings/utils/performance-alerts';

export const MANAGE_PERMISSION_REQUIRED_TOOLTIP =
  'You don’t have access to edit performance alerts. Please contact your plan administrator to enable this permission.';

export const EMAIL_RECIPIENT_DELETED_TOOLTIP =
  'The Dash Social user associated to this email has been deleted. They will not receive any performance alert emails.';

export const EMAIL_RECIPIENT_INACTIVE_TOOLTIP =
  'The Dash Social user associated to this email is currently inactive. They will not receive any performance alert emails.';

export const PERFORMANCE_ALERT_ISSUE_TOOLTIP =
  'This performance alert has errors. Edit the performance alert to resolve the issues.';

export const DEPRECATED_METRIC_TOOLTIP =
  'This performance alert uses a deprecated metric that is no longer available. Please update this performance alert to use supported metrics.';

export const DISCONTINUED_METRIC_SECTION_TITLE = 'Discontinued';

export const DISCONTINUED_METRIC_SECTION_TOOLTIP =
  'These metrics are no longer supported and may stop receiving data';

export const DISCONTINUED_METRIC_OPTION_TOOLTIP =
  'This metric has been discontinued and is no longer available';

export const INFO_BANNER_MESSAGE =
  'Your agent can notify you when your posts hit a performance threshold. Set custom alerts & actions by channel, metric, and media type to catch momentum early and respond while it matters.';

export const LEARN_MORE_URL = 'https://help.dashsocial.com/hc/en-us/articles/46922310016397';

export const ERROR_BANNER_MESSAGE =
  'One or more of your performance alerts below has an error and is not running. Edit or delete this alert to resolve the issue.';

export const DELETE_CONFIRMATION_TITLE = 'Delete Performance Alert?';
export const DELETE_CONFIRMATION_MESSAGE =
  'This will permanently remove this performance alert. You will no longer receive alerts for the thresholds configured here and any content previously impacted by this performance alert will remain unchanged.';

export const DATE_FORMAT = 'MMM DD, YYYY';
export const MAX_VISIBLE_BRANDS = 3;
export const SKELETON_COUNT = 5;

// Max brand users fetched for the email-recipient picker and for reconciling
// email recipients against the selected brands (shared so both hit the same cache).
export const USER_FETCH_LIMIT = 1000;

export const SENTIMENT_LABELS = {
  positive: 'Positive',
  negative: 'Negative',
  neutral: 'Neutral',
} as const;

export const FILTER_KEYWORD_TOOLTIP =
  'Input specific keywords, mentions and hashtags. Keyword filter is exact matches only.';

export const FILTER_DATE_PUBLISHED_TOOLTIP =
  'Set a window for when posts were published. This can be in the past or future.';

export const FILTER_SENTIMENT_TOOLTIP = 'Filter posts by positive, negative, or neutral sentiment';

export const FILTER_KEYWORD_PLACEHOLDER = 'Enter keyword...';

export const SENTIMENT_FILTER_UPSELL_MESSAGE =
  'Unlock Social Listening to apply sentiment filters to your alerts';

export const SENTIMENT_FILTER_TIKTOK_UGC_TOOLTIP = 'TikTok UGC does not support this filter';

export const TIKTOK_UGC_THRESHOLD_SENTIMENT_TOOLTIP =
  'TikTok UGC does not support sentiment filters. Remove the filter to add a TikTok UGC threshold.';

export const YOUTUBE_THRESHOLD_SENTIMENT_TOOLTIP =
  'Sentiment filters are not available when a YouTube threshold is added.';

export const THRESHOLD_COMPARISON_LABELS = {
  exceeds: 'Exceeds',
} as const;

export const PERFORMANCE_ALERT_ACTIONS = {
  SEND_EMAIL: 'Send Email To',
  ADD_CONTENT_TAGS: 'Add Content Tags',
  ADD_TO_CAMPAIGNS: 'Add to Campaigns',
  ADD_TO_BOARDS: 'Add to Boards',
  ADD_TO_GALLERIES: 'Add to Galleries',
} as const;

export const PERFORMANCE_ALERT_ACTION_TOOLTIPS = {
  SEND_EMAIL: "Automatically send an email alert to users when a post meets this alert's criteria",
  ADD_CONTENT_TAGS:
    "Apply tags to posts that meet this alert's criteria for easy filtering and reporting",
  ADD_TO_CAMPAIGNS:
    "Add posts that meet this alert's criteria to campaigns for cross-channel analysis",
  ADD_TO_BOARDS:
    "Sort posts that meet this alert's criteria into boards for in-depth creative analysis by channel",
  ADD_TO_GALLERIES: "Organize posts that meet this alert's criteria into your Library",
} as const;

export const SORT_OPTIONS = [{ label: 'Alphabetically', value: 'ALPHABETICALLY' }] as const;

export const DEFAULT_SORT = {
  sortOrder: 'ASC',
  selected: SORT_OPTIONS[0],
} as const;

export const PERFORMANCE_ALERT_STEP_ONE_INVALID_TOOLTIP =
  'You must specify which brands will have their content monitored';

export const PERFORMANCE_ALERT_STEP_TWO_INVALID_TOOLTIP =
  'You must specify a threshold and a valid action';

export const PERFORMANCE_ALERT_BRAND_DISABLED_TOOLTIP =
  'Brand must have access to performance alerts';

export const PERFORMANCE_ALERT_BRAND_TAG_DISABLED_TOOLTIP =
  'At least one brand in the tag must have access to performance alerts';

export const PERFORMANCE_ALERT_CANNOT_USE_BRAND_TAGS_TOOLTIP =
  'You don’t have access to create a performance alert with Brand Tags';

export type ThresholdCategory = 'OWNED' | 'UGC';

export type ThresholdPresetType = 'HIGH_PERFORMING' | 'EXCEPTIONAL' | 'RARE_BREAKOUT' | 'CUSTOM';

export type ThresholdChannelOption = {
  label: string;
  value: ResolveBenchmarksRequestBodyChannel;
  category: ThresholdCategory;
};

export type ThresholdMediaOption = {
  label: string;
  value: PerformanceAlertMediaType;
};

export const METRIC_KEY_PATTERN = /^[A-Z0-9_]+$/;

export const PERCENTILE_OPTIONS = [75, 85, 95] as const;

export const LOOKBACK_PERIOD_OPTIONS = [
  { label: 'Last 90 Days', value: 90 },
  { label: 'Last 60 Days', value: 60 },
  { label: 'Last 30 Days', value: 30 },
] as const;

export const SOURCE_TYPE_OPTIONS: Array<{ label: string; value: PerformanceAlertSourceType }> = [
  { label: 'Organic Posts Only', value: 'ORGANIC_ONLY' },
  { label: 'Promoted Posts Only', value: 'PROMOTED_ONLY' },
  { label: 'Organic & Promoted Posts', value: 'ORGANIC_AND_PROMOTED' },
];

export const MEDIA_TYPE_OPTIONS: ThresholdMediaOption[] = [
  { label: 'All Media', value: 'ALL_MEDIA' },
  { label: 'Carousels', value: 'CAROUSEL' },
  { label: 'Photos', value: 'PHOTO' },
  { label: 'Images', value: 'IMAGE' },
  { label: 'Reels', value: 'REEL' },
  { label: 'Videos', value: 'VIDEO' },
  { label: 'Live', value: 'LIVE' },
  { label: 'On-Demand', value: 'ON_DEMAND' },
  { label: 'Shorts', value: 'SHORTS' },
  { label: 'Documents', value: 'DOCUMENT' },
  { label: 'Links', value: 'LINK' },
  { label: 'Text', value: 'TEXT' },
  { label: 'Audio', value: 'AUDIO' },
];

export const CHANNEL_OPTIONS: ThresholdChannelOption[] = [
  { label: 'Instagram', value: 'INSTAGRAM', category: 'OWNED' },
  { label: 'Instagram Story', value: 'INSTAGRAM_STORY', category: 'OWNED' },
  { label: 'TikTok', value: 'TIKTOK', category: 'OWNED' },
  { label: 'Facebook', value: 'FACEBOOK', category: 'OWNED' },
  { label: 'Pinterest', value: 'PINTEREST', category: 'OWNED' },
  { label: 'YouTube', value: 'YOUTUBE', category: 'OWNED' },
  { label: 'LinkedIn', value: 'LINKEDIN', category: 'OWNED' },
  { label: 'Threads', value: 'THREADS', category: 'OWNED' },
  { label: 'X', value: 'X', category: 'OWNED' },
  { label: 'Snapchat', value: 'SNAPCHAT', category: 'OWNED' },
  { label: 'Instagram', value: 'INSTAGRAM_UGC', category: 'UGC' },
  { label: 'TikTok', value: 'TIKTOK_UGC', category: 'UGC' },
];

export const CATEGORY_OPTIONS: Array<{ label: string; value: ThresholdCategory }> = [
  { label: 'Owned', value: 'OWNED' },
  { label: 'UGC', value: 'UGC' },
];

// Maps a threshold channel (the API benchmark channel enum) onto the board
// channel key used by the board picker (CHANNEL_TO_SUPPORTED_BOARD /
// CHANNEL_TO_BOARD_PERMISSION).
// UGC channels collapse onto their parent channel because there are no separate
// UGC boards.
export const THRESHOLD_CHANNEL_TO_BOARD_CHANNEL: Partial<
  Record<ResolveBenchmarksRequestBodyChannel, string>
> = {
  INSTAGRAM: 'INSTAGRAM',
  INSTAGRAM_STORY: 'INSTAGRAM_STORY',
  INSTAGRAM_UGC: 'INSTAGRAM',
  TIKTOK: 'TIKTOK',
  TIKTOK_UGC: 'TIKTOK',
  FACEBOOK: 'FACEBOOK',
  PINTEREST: 'PINTEREST',
  YOUTUBE: 'YOUTUBE',
  LINKEDIN: 'LINKEDIN',
  THREADS: 'THREADS',
  X: 'X',
};

export const CUSTOM_THRESHOLD_TOOLTIP = 'Alert triggers when this value is exceeded.';

export const AD_ACCOUNT_REQUIRED_BANNER_MESSAGE =
  'Connect to your Ad account to access promoted content.';

export const AD_ACCOUNT_REQUIRED_BREAKDOWN_BANNER_MESSAGE =
  'Connect your Ad account to filter by organic-only or promoted-only content.';

export const AD_ACCOUNT_REQUIRED_BANNER_CTA = 'Connect Account';

export const AD_ACCOUNT_REQUIRED_METRIC_TOOLTIP = 'This metric type requires an ad account';

export const AD_ACCOUNT_REQUIRED_SOURCE_TYPE_TOOLTIP = 'This post type requires an ad account';

export const ALL_MEDIA_TOOLTIP =
  'When All Media is selected, thresholds are calculated separately for each media type — not across all types combined.';

export const LOOKBACK_PERIOD_TOOLTIP =
  'The benchmark is calculated using your posts published within this time period';

export const THRESHOLD_PRESET_OPTIONS: Array<{
  label: string;
  value: ThresholdPresetType;
  percentile?: 75 | 85 | 95;
}> = [
  { label: 'High Performing', value: 'HIGH_PERFORMING', percentile: 75 },
  { label: 'Exceptional', value: 'EXCEPTIONAL', percentile: 85 },
  { label: 'Rare Breakout', value: 'RARE_BREAKOUT', percentile: 95 },
  { label: 'Custom', value: 'CUSTOM' },
];

export const PRESET_PERCENTILE_BY_TYPE: Record<ThresholdPresetType, 75 | 85 | 95> = {
  HIGH_PERFORMING: 75,
  EXCEPTIONAL: 85,
  RARE_BREAKOUT: 95,
  CUSTOM: 75,
};

export const PRESET_TYPE_BY_PERCENTILE: Record<75 | 85 | 95, ThresholdPresetType> = {
  75: 'HIGH_PERFORMING',
  85: 'EXCEPTIONAL',
  95: 'RARE_BREAKOUT',
};

export const PRESET_TOOLTIP_BY_TYPE: Record<ThresholdPresetType, string> = {
  HIGH_PERFORMING: 'Top 25% of your recent posts',
  EXCEPTIONAL: 'Top 15% of your recent posts',
  RARE_BREAKOUT: 'Top 5% of your recent posts',
  CUSTOM: '',
};

// Source type labels shared across the threshold builder's step header sentence
// (e.g. "Instagram Reels - Organic & Promoted Posts") and the threshold chips on
// both the builder and the alert cards, so all surfaces read identically.
export const SOURCE_TYPE_LABELS: Record<PerformanceAlertSourceType, string> = {
  ORGANIC_ONLY: 'Organic',
  PROMOTED_ONLY: 'Promoted',
  ORGANIC_AND_PROMOTED: 'Organic & Promoted',
};

export const MEDIA_TYPE_HEADER_LABELS: Record<PerformanceAlertMediaType, string> = {
  ALL_MEDIA: 'All Media',
  CAROUSEL: 'Carousels',
  PHOTO: 'Photos',
  IMAGE: 'Images',
  REEL: 'Reels',
  VIDEO: 'Videos',
  LIVE: 'Live',
  ON_DEMAND: 'On-Demand',
  SHORTS: 'Shorts',
  DOCUMENT: 'Documents',
  LINK: 'Links',
  TEXT: 'Text',
  AUDIO: 'Audio',
};
